<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { slide } from "svelte/transition"
  import { xhr, toast, wait } from "$app/utils"
  import Button from "$app/components/ButtonSpinner.svelte"
  import Navigation from "./Navigation.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"
  import bg from "@images/onboarding/bg-eclipse.jpg"
  import img from "@images/onboarding/email-settings.png"

  console.log("loading EmailSettings")

  interface Props {
    onUpdateStep: (step: string) => void
    onUpdateUser: (user: any) => void
    steps: any[]
  }

  let { onUpdateStep, onUpdateUser, steps } = $props()

  let form = $state({
    send_daily_updates: null,
    send_weekly_report: null,
    weekly_report_format: null,
    send_push_notifications: appStore.current_user.send_push_notifications,
    send_notification_summary: appStore.current_user.send_notification_summary
  })

  let loadingPlanSettings = $state(false)

  async function getPlanUserSettings() {
    loadingPlanSettings = true
    try {
      const resp = await xhr.get(
        `/user_onboarding/${appStore.current_user.id}/plan_user_settings?plan_id=${appStore.plan.id}`
      )
      console.log("resp", resp.data)
      let settings = resp.data
      form.send_daily_updates = settings.send_daily_updates
      form.send_weekly_report = settings.send_weekly_report
      form.weekly_report_format = settings.weekly_report_format
    } catch (e: any) {
      console.log("getPlanUserSettings")
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }

      toast("There was an error updating your preferences", "error")
    } finally {
      loadingPlanSettings = false
    }
  }

  async function saveGlobalEmailSettings() {
    try {
      await wait(100)
      const resp = await xhr.patch(
        `/user_onboarding/${appStore.current_user.id}`,
        {
          user: {
            send_push_notifications: form.send_push_notifications,
            send_notification_summary: form.send_notification_summary
          }
        }
      )

      toast("Email notification settings have been saved.", "success")
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating your preferences", "error")
    }
  }

  const savePlanUserSettings = async function () {
    try {
      await wait(100)
      const resp = await xhr.patch(
        `/user_onboarding/${appStore.current_user.id}/update_plan_user_settings`,
        {
          plan_id: appStore.plan.id,
          plan_user_role: {
            send_daily_updates: form.send_daily_updates,
            send_weekly_report: form.send_weekly_report,
            weekly_report_format: form.weekly_report_format
          }
        }
      )
      toast("Email notification settings have been saved.", "success")
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating your preferences", "error")
    }
  }

  let saving = $state(false)
  async function updateUser(): Promise<any> {
    saving = true
    const formData = new FormData()
    formData.append("user[onboarding_steps][email_settings]", "true")

    try {
      const resp = await xhr.patch(
        `/user_onboarding/${appStore.current_user.id}`,
        formData
      )
      onUpdateUser(resp.data)
    } catch (e) {
      console.log("updateUser")
      console.error(e)
      toast("There was an error updating your preferences", "error")
    } finally {
      saving = false
    }
  }

  onMount(() => {
    getPlanUserSettings()
  })
</script>

<div class="mx-auto px-2 lg:px-6 pt-4 w-full pb-8 lg:pb-20 lg:text-left">
  <div class="px-4 w-full md:w-2/3 lg:w-1/2 xl:w-5/12 md:pr-12 xl:pr-16">
    <Navigation step="email_settings" {onUpdateStep} {steps} />

    <h2
      class="mt-6 md:mt-12 text-3xl md:text-5xl font-bold text-left text-white"
    >
      Email Settings
    </h2>

    <p class="mt-4 md:mt-12 text-base md:text-3xl text-white">
      Choose the emails you would like to receive.
    </p>

    {#if loadingPlanSettings}
      <div class="py-6 flex justify-center w-full">
        <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
      </div>
    {:else}
      <div class="bg-indigo-100 rounded-lg py-2 px-6 my-4">
        <div class="mt-6 grid grid-cols-3 gap-y-2 gap-x-4">
          <div
            class="col-span-2 sm:col-span-1 flex items-center justify-start sm:justify-end"
          >
            <label
              for="email"
              class="mt-0 pt-0 text-sm font-medium text-gray-700"
            >
              Send Daily Plan Updates?
            </label>
          </div>
          <div class="flex items-center justify-start col-span-1 sm:col-span-2">
            <div class="max-w-lg flex items-center">
              <label class="cursor-pointer mt-0 pt-0">
                <input
                  type="radio"
                  name="send_daily_updates"
                  bind:group={form.send_daily_updates}
                  onchange={savePlanUserSettings}
                  value={true}
                />
                <span>Yes</span>
              </label>
              <label class="ml-3 cursor-pointer mt-0 pt-0">
                <input
                  type="radio"
                  name="send_daily_updates"
                  bind:group={form.send_daily_updates}
                  onchange={savePlanUserSettings}
                  value={false}
                />
                <span>No</span>
              </label>
            </div>
          </div>
          <div class="col-span-3 sm:col-start-2 sm:col-span-2">
            <p class="text-gray-500 text-sm">
              Send daily summary of activity including recognitions and
              goal/task updates.
            </p>
          </div>
        </div>
        <div class="mt-6 grid grid-cols-3 gap-y-2 gap-x-4">
          <div
            class="col-span-3 sm:col-span-1 flex items-center justify-start sm:justify-end"
          >
            <label
              for="email"
              class="mt-0 pt-0 text-sm font-medium text-gray-700"
            >
              Send Weekly Activity Report?
            </label>
          </div>
          <div class="flex items-center justify-start col-span-3 sm:col-span-2">
            <div class="max-w-lg flex items-center">
              <label class="cursor-pointer mt-0 pt-0">
                <input
                  type="radio"
                  name="send_weekly_report"
                  bind:group={form.send_weekly_report}
                  onchange={savePlanUserSettings}
                  value={true}
                />
                <span>Yes</span>
              </label>
              <label class="ml-3 cursor-pointer mt-0 pt-0">
                <input
                  type="radio"
                  name="send_weekly_report"
                  bind:group={form.send_weekly_report}
                  value={false}
                  onchange={savePlanUserSettings}
                />
                <span>No</span>
              </label>
            </div>
          </div>
          <div class="col-span-3 sm:col-start-2 sm:col-span-2">
            <p class="text-gray-500 text-sm">
              Send weekly progress reminder email and weekly activity page
              report.
            </p>
          </div>
        </div>

        {#if form.send_weekly_report}
          <div class="mt-6 grid grid-cols-3 gap-y-2 gap-x-4" transition:slide>
            <div
              class="col-span-2 sm:col-span-1 flex items-center justify-start sm:justify-end"
            >
              <label
                for="email"
                class="mt-0 pt-0 text-sm font-medium text-gray-700"
              >
                Weekly Activity Report Format
              </label>
            </div>
            <div
              class="flex items-center justify-start col-span-3 sm:col-span-2"
            >
              <div class="max-w-xl flex rounded-md shadow-sm">
                <select
                  name="weekly_meeting_day"
                  class="w-40"
                  bind:value={form.weekly_report_format}
                  onchange={savePlanUserSettings}
                >
                  <option value="full">Full Report</option>
                  <option value="summary">Summary Report</option>
                </select>
              </div>
            </div>
            <div class="col-span-3 sm:col-start-2 sm:col-span-2">
              <p class="text-gray-500 text-sm">
                The full report includes a snapshot of the full Activity page,
                the summary report only includes highlights.
              </p>
            </div>
          </div>
        {/if}

        <div class="mt-6 grid grid-cols-3 gap-y-2 gap-x-4">
          <div
            class="col-span-2 sm:col-span-1 flex items-center justify-start sm:justify-end"
          >
            <label
              for="email"
              class="mt-0 pt-0 text-sm font-medium text-gray-700"
            >
              Send Notification Summary?
            </label>
          </div>
          <div class="flex items-center justify-start col-span-3 sm:col-span-2">
            <div class="max-w-lg flex items-center">
              <label class="cursor-pointer mt-0 pt-0">
                <input
                  type="radio"
                  name="send_notification_summary"
                  bind:group={form.send_notification_summary}
                  onchange={saveGlobalEmailSettings}
                  value={true}
                />
                <span>Yes</span>
              </label>
              <label class="ml-3 cursor-pointer mt-0 pt-0">
                <input
                  type="radio"
                  name="send_notification_summary"
                  bind:group={form.send_notification_summary}
                  onchange={saveGlobalEmailSettings}
                  value={false}
                />
                <span>No</span>
              </label>
            </div>
          </div>
          <div class="col-span-3 sm:col-start-2 sm:col-span-2">
            <p class="text-gray-500 text-sm">
              Send summary of un-read notifications from the previous day.
            </p>
          </div>
        </div>
      </div>
      <div class="pt-10">
        <Button
          onclick={updateUser}
          processing={saving}
          class="btn-rounded bg-green-600 hover:bg-green-700 text-white btn-big w-full text-lg font-bold md:w-60 h-14"
        >
          Finish and Go to Plan
        </Button>
      </div>
    {/if}
  </div>
</div>
<div
  class="relative w-full h-64 hidden md:inline md:h-96 lg:absolute lg:inset-y-0 lg:-right-0 md:w-1/3 lg:w-1/2 xl:w-7/12 lg:h-full bg-lwp-light-grey bg-right-bottom bg-no-repeat bg-contain"
  style="background-image: url({bg})"
>
  <img
    src={img}
    alt="Your Profile"
    class="absolute inset-0 lg:-inset-x-8 lg:inset-y-6 w-11/12 object-fill"
  />
</div>
